import React, { useEffect, useState, useMemo } from "react";
import SmuButton from "../../utils/smu-button/SmuButton";
import { Card, Col, Form, Row } from "react-bootstrap";
import { IconBasket, IconPencilEdit } from "../../icons/Icon";
import SmuBadge from "../../utils/smu-badge/SmuBadge";
import { COLOR_PRIMARY } from "../../constants/colors";
import SmuUserIcon from "../../utils/smu-user-icon/SmuUserIcon";
import EditUser from "../edit-user/EditUser";
import { prepareText } from "../../helpers";
import { toast } from "../../helpers/smu-alerts/smu-lerts";
import loadingGif from "../../assets/images/loading.gif";
import moment from "moment";
import { deleteUser, getUsers, signUp } from "../../api/users";
import Swal from "sweetalert2";
import _ from "lodash";
import SmuTable from "../../utils/smu-table/SmuTable";
import SmuSearch from "../../utils/smu-search/SmuSearch";

const UsersTable = () => {
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const [username, setUsername] = useState("");
  const [profileId, setProfileId] = useState("");
  const [creating, setCreating] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [users, setUsers] = useState([]);
  const [handleUsers, setHandleUsers] = useState([]);

  const handleGetUsers = () => {
    getUsers().then(({ data }) => {
      setUsers(data.users);
    });
  };

  useEffect(() => {
    let _users = users;

    if (!!search) {
      _users = _users.filter((_user) => {
        const _search = prepareText(search);
        const name = prepareText(_user.name);
        const datetime = prepareText(_user.timestamp);
        const username = prepareText(_user.email);
        return (
          name.includes(_search) ||
          datetime.includes(_search) ||
          username.includes(_search)
        );
      });
    }

    _users = _.orderBy(_users, ["timestamp"], ["desc"]);

    setHandleUsers(_users);
  }, [users, search]);



  useEffect(() => {
    handleGetUsers();
  }, []);

  const provideAccess = (event) => {
    event.preventDefault();
    if (!username) return;

    setCreating(true);
    const _user = users.find((u) => {
      return u.email.toUpperCase() === username.toUpperCase();
    });
    if (!!_user) {
      setCreating(false);
      return toast({
        icon: "warning",
        title: "¡El nombre de usuario ya existe!",
      });
    }

    const newUser = new FormData();

    newUser.append("username", username);
    newUser.append("name", username);
    newUser.append("status", 1);
    newUser.append("admin", profileId == 1);
    newUser.append("user_type", "LDAP");

    signUp(newUser)
      .then(({ error }) => {
        if (!!error) throw new Error(error.message);

        setUsers((prevUsers) => [newUser, ...prevUsers]);
        toast({ icon: "success", title: "¡Usuario creado con éxito!" });
      })
      .catch((err) => {
        console.error(err);//toast({ icon: "error", title: err });
      })
      .finally(() => {
        handleGetUsers();
        setUsername("");
        setProfileId("");
        setCreating(false);
      });
  };

  const editUser = async (newUser) => {
    if (newUser.values().length <= 0) return;

    return signUp(newUser)
      .then(({ error }) => {
        if (!!error) throw new Error(error.message);

        setUsers((prevUsers) => {
          return prevUsers.map((u) => {
            return u.username === newUser.username ? newUser : u;
          });
        });
        toast({ icon: "success", title: "¡Usuario actulizado!" });
      })
      .catch((err) => {
        console.error(err);//toast({ icon: "error", title: err });
      })
      .finally(() => handleGetUsers());
  };

  const handleDelete = (_username) => {
    Swal.fire({
      title: "¿Deseas eliminar este usuario?",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      confirmButtonColor: COLOR_PRIMARY,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      deleteUser(_username)
        .then(({ error }) => {
          if (!!error) throw new Error(error?.message);
          toast({ icon: "success", title: "Usuario eliminado." });
        })
        .catch((err) => {
          console.error(err);//toast({ icon: "error", title: err });
        })
        .finally(() => {
          handleGetUsers();
        });
    });
  };

  const columns = [
    {
      name: "",
      cell: () => (
        <div className="cell">
          <SmuUserIcon color="#ec6c44" />
        </div>
      ),
      width: "80px",
    },
    {
      name: "Nombre",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="cell text-truncate">{row?.name || row.email}</div>
      ),
      width: "25%",
    },
    {
      name: "Username",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => <div className="cell">{row.email}</div>,
      width: "20%",
    },
    {
      name: "Perfil",
      selector: (row) => row.admin,
      sortable: true,
      cell: (row) => (
        <div className="cell">{(row.admin == true || row.admin == 'true') ? "Admin" : "Usuario"}</div>
      ),
      width: "15%",
    },
    {
      name: "Fecha Validación",
      selector: (row) => row.timestamp,
      sortable: true,
      cell: (row) => (
        <div className="cell">
          {moment(row.timestamp).format("DD-MM-YYYY HH:mm")}
        </div>
      ),
    },
    {
      name: "",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="cell d-flex">
          <SmuButton _className="ms-3" control={() => setUserToEdit(row)}>
            <IconPencilEdit color="#33383F" />
          </SmuButton>
          <SmuButton
            _className="ms-3"
            control={() => handleDelete(row.email)}
          >
            <IconBasket color="#33383F" />
          </SmuButton>
        </div>
      ),
      right: true,
    },
  ];

  return (
    <>
      <EditUser
        user={userToEdit}
        onHide={() => setUserToEdit(null)}
        editUser={editUser}
      />

      <Card className="smu-card">
        <Card.Header className="d-flex flex-column py-4">
          <div className="d-flex align-items-center me-auto">
            <SmuBadge color={COLOR_PRIMARY} />
            <span className="fs-5 ms-3 me-auto">Buscar Usuarios</span>
          </div>
          <Form
            className="smu-form px-5 mx-4 my-5"
            onSubmit={(event) => provideAccess(event)}
          >
            <Row className="d-flex">
              <Col col={12} className="col-5">
                <Form.Control
                  type="search"
                  placeholder="Nombre de usuario"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                />
              </Col>
              <Col col={12} className="col-4">
                <Form.Select
                  value={profileId}
                  onChange={(event) => setProfileId(event.target.value)}
                >
                  <option value={0}>Seleccionar Perfil*</option>
                  <option value={1}>Admin</option>
                  <option value={2}>Usuarios</option>
                </Form.Select>
              </Col>
              <Col col={12}>
                {!creating ? (
                  <button
                    type="submit"
                    className="smu-generic-button w-100 text-truncate w-100 h-100"
                  >
                    Dar Acceso
                  </button>
                ) : (
                  <div className="smu-generic-button h-100 d-flex">
                    <img
                      src={loadingGif}
                      alt="Cargando"
                      className="m-auto"
                      width={28}
                      height={28}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Form>
        </Card.Header>
        <Card.Header className="d-flex py-4">
          <div className="d-flex align-items-center me-auto">
            <SmuBadge color={COLOR_PRIMARY} />
            <span className="fs-5 ms-3 me-auto">Usuarios agregados</span>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col col={12} xs={4} className="offset-8">
              <SmuSearch
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </Col>
          </Row>
          <SmuTable
            columns={columns}
            data={handleUsers}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default UsersTable;
